import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { trackMeta } from "../lib/metaPixel";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = not authed
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        const token = localStorage.getItem("gpm_token");
        if (!token) {
            setUser(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch (e) {
            localStorage.removeItem("gpm_token");
            setUser(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("gpm_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            // 403 with structured detail means email_not_verified — propagate so UI can redirect.
            const detail = e.response?.data?.detail;
            if (e.response?.status === 403 && typeof detail === "object" && detail?.code === "email_not_verified") {
                return { ok: false, needsVerification: true, email: detail.email || email, error: detail.message };
            }
            return { ok: false, error: formatApiError(detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            // New flow: backend returns { verification_required: true, email, message } and NO token.
            // CompleteRegistration only fires AFTER OTP verification (see verifyOtp) — a pending user
            // is not a "registered" user yet from an analytics standpoint.
            if (data?.verification_required) {
                return { ok: true, needsVerification: true, email: data.email, message: data.message };
            }
            // Fallback for legacy flow (still returning token) — fire CompleteRegistration now.
            if (data?.token) {
                localStorage.setItem("gpm_token", data.token);
                setUser(data.user);
                try {
                    trackMeta("CompleteRegistration",
                        { status: true, content_name: "user_signup", registration_method: "email", user_role: data.user?.role },
                        { email: payload?.email, phone: payload?.phone, external_id: data.user?.id }
                    );
                } catch { /* silent */ }
                return { ok: true, user: data.user };
            }
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const verifyOtp = async (email, otp) => {
        try {
            const { data } = await api.post("/auth/verify-otp", { email, otp });
            if (data.token) {
                localStorage.setItem("gpm_token", data.token);
                setUser(data.user);
                // Successful registration completes here for the OTP-based flow.
                try {
                    trackMeta("CompleteRegistration",
                        { status: true, content_name: "user_signup", registration_method: "email_otp", user_role: data.user?.role },
                        { email: data.user?.email || email, phone: data.user?.phone, external_id: data.user?.id }
                    );
                } catch { /* silent */ }
            }
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const resendOtp = async (email) => {
        try {
            await api.post("/auth/resend-otp", { email });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e.response?.data?.detail) || e.message };
        }
    };

    const logout = () => {
        localStorage.removeItem("gpm_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, verifyOtp, resendOtp, refresh: fetchMe }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}
